import { removeAllEmTags } from "common/StringUtils";
import { ProductListItem } from "inventory/list/InventoryListReducer";
import { groupBy, isEmpty } from "lodash";
import { aggregatePackInventory } from "./aggregatePackInventory";

export const mapPacksToProductListItem = (
  products: ProductListItem[],
  packs: ProductListItem[],
  shouldShowParentPack?: boolean
): ProductListItem[] => {
  const validPacks = packs.filter((pack) => !isEmpty(removeAllEmTags(pack?.packOf ?? "")));
  const packsByPackOf = groupBy(validPacks, (pack) => removeAllEmTags(pack.packOf!));

  return products.flatMap((product) => {
    const productPacks: ProductListItem[] = packsByPackOf[removeAllEmTags(product.dsku)] || [];
    if (shouldShowParentPack) {
      const parentPack = buildPackFromParentProduct(product, packs);
      productPacks.push(parentPack);
    }
    return productPacks.map((pack) => mergePackAndProductListItems(product, pack, shouldShowParentPack));
  });
};

const mergePackAndProductListItems = (
  product: ProductListItem,
  pack: ProductListItem,
  shouldShowParentPack?: boolean
): ProductListItem => {
  const packInventories = aggregatePackInventory(product, pack, shouldShowParentPack);
  const name = removeAllEmTags(product.name);
  const msku = product.msku ? removeAllEmTags(product.msku) : product.msku;
  const asin = product.asin ? removeAllEmTags(product.asin) : undefined;
  const productAliases = product.productAliases;
  const barcodes = product?.barcodes?.map((barcode) => removeAllEmTags(barcode));
  const unitsSoldLast30Days = product?.unitsSoldLast30Days;
  const productInventories = {
    onHandQty: product.onHandQty,
    unfilledOrderQty: product.unfilledOrderQty,
    nonPickableQty: product.nonPickableQty,
    unavailableQty: product.unavailableQty,
    inTransferQty: product.inTransferQty,
  };

  return {
    ...pack,
    name,
    productAliases,
    msku,
    asin,
    barcodes,
    raw: {
      ...pack.raw,
      name,
      msku,
      asin,
      productAliases,
      barcodes,
      ...packInventories,
      ...productInventories,
      unitsSoldLast30Days,
      isKit: product.isKit,
    },
    ...packInventories,
    ...productInventories,
    unitsSoldLast30Days,
    isKit: product.isKit,
  };
};

const getDefaultPack = (packs: ProductListItem[], product: ProductListItem): ProductListItem | undefined => {
  return packs.find((pack) => pack.isDefaultPack && pack.packOf === product.dsku);
};

/**
 * Builds a pack from the parent product.
 * @param product - The parent product.
 * @param packs - The list of packs.
 * @returns A product list item representing the pack.
 */
export const buildPackFromParentProduct = (product: ProductListItem, packs: ProductListItem[]): ProductListItem => {
  const defaultCasePack = getDefaultPack(packs, product);

  const packProperties = {
    caseQty: defaultCasePack?.caseQty ?? 1,
    packOf: product.dsku,
    length: defaultCasePack?.length ?? product.length,
    width: defaultCasePack?.width ?? product.width,
    height: defaultCasePack?.height ?? product.height,
    weight: defaultCasePack?.weight ?? product.weight,
  };

  return {
    ...product,
    ...packProperties,
    raw: {
      ...product.raw,
      ...packProperties,
    },
  };
};
